import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, ElementRef, ViewChild, Renderer2 } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs/internal/Subscription';
import { MenuOption } from '../models/menu-option';

@Component({
    selector: 'lib-autocomplete',
    templateUrl: './autocomplete.component.html',
    styleUrls: ['./autocomplete.component.scss'],
    standalone: false
})
export class AutocompleteComponent implements OnInit, OnDestroy {
  #subscriptions: Subscription = new Subscription();
  #options: Array<MenuOption>;

  @Input() defaultOption: MenuOption;
  @Input() dividerIndex?: number = null;
  @Input() error?: string;
  @Input() fullWidth: boolean = false;
  @Input() isInvalid: boolean = false;
  @Input() placeholder: string;
  @Input() title: string;
  @Input() name: string;
  @Input() trackingIdPrefix?: string;

  @Input() set options(value: Array<MenuOption>) {
    this.#options = value;
    this.filteredOptions = this.options;
  }
  get options(): Array<MenuOption> {
    return this.#options;
  }

  @Input() set disabled(value: boolean){
    if(value){
      this.search.disable();
    }
    else {
      this.search.enable();
    }
  }
  get disabledState(): boolean {
    return this.search.disabled;
  }

  @Output() optionSelected = new EventEmitter<MenuOption>();

  filteredOptions: MenuOption[];
  showMenu: boolean = false;
  filtering: boolean = false;
  search: UntypedFormControl = new UntypedFormControl('');
  @ViewChild('searchInput') searchInput: ElementRef;

  constructor(private renderer: Renderer2){}

  ngOnInit(): void {
    this.filteredOptions = this.options;

    if(!!this.defaultOption){
      this.updateSearchValue(this.defaultOption.value);
    }

    this.#subscriptions.add(
      this.search.valueChanges.subscribe(value => {
        this.filterOptions();
        this.setShowMenu(true);
        if(value.length === 0){
          this.filtering = false;
        }
      })
    );
  }

  setShowMenu(open: boolean): void {
    if(!this.disabledState) {
      this.showMenu = open;
      if(open && !this.filtering) {
        this.renderer.selectRootElement(this.searchInput.nativeElement).select();
      }
    }
    else{
      this.showMenu = false;
    }
  }

  updateSearchValue(value: string): void {
    this.search.patchValue(value);
  }

  filterOptions(): void {
    const searchValue = this.search.value as string;
    if(searchValue.length === 0){
      this.#deletedSelectedOption();
    }
    else {
      this.filtering = true;
      this.filteredOptions = this.options.map(option => {
        let label = option.label;
        const index = label.toLowerCase().indexOf(searchValue.toLowerCase());
        const filteredOption = {
          id: option.id,
          value: option.value,
          label:
            index === -1
              ? option.label
              : `${option.label.substring(0, index)}<b>${option.label.substring(index,index + searchValue.length)}</b>${option.label.substring(index + searchValue.length)}`, //Case insensitive replace
        };

        return filteredOption;
      });

      this.filteredOptions = this.filteredOptions.filter(option => option.label.toLowerCase().includes(searchValue.toLowerCase()));
    }
  }

  selectOption(option: MenuOption): void {
    this.updateSearchValue(option.value);
    this.setShowMenu(false);
    this.filteredOptions = this.options;
    this.filtering = false;
    this.optionSelected.emit(option);
  }

  #deletedSelectedOption(): void {
    this.filtering = false;
    this.optionSelected.emit({label: '', value: ''} as MenuOption);
  }

  ngOnDestroy(): void {
    this.#subscriptions.unsubscribe();
  }
}
